/**
 * Sidebar — navegación por sección + footer de marca.
 *
 * Desktop: botón "Minimizar" colapsa a solo iconos.
 * Móvil (prop `mobile`): se usa como contenido del drawer; sin colapsar y
 * cierra el drawer al navegar (`onNavigate`).
 *
 * Los items se filtran por rol del usuario.
 */
import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useDevEnabled } from '@/lib/useDevEnabled'
import { TomMark } from '@/components/ui/TomMark'
import { navItemsFor } from './navItems'

export function Sidebar({ mobile = false, onNavigate }) {
  const { user } = useAuth()
  const devEnabled = useDevEnabled()
  const [collapsedState, setCollapsed] = useState(false)
  const collapsed = mobile ? false : collapsedState
  const role = user?.role || 'user'
  const items = navItemsFor(role, devEnabled)

  return (
    <aside
      className={[
        mobile ? 'w-72 max-w-[82vw]' : collapsed ? 'w-20' : 'w-72',
        'flex-shrink-0 bg-expo-dark h-full flex flex-col transition-[width] duration-200',
      ].join(' ')}
    >
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-2">
          {items.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                onClick={() => onNavigate?.()}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  [
                    'w-full flex items-center rounded-xl transition-all text-[18px]',
                    collapsed ? 'justify-center px-0 py-3' : 'gap-3 px-4 py-3',
                    isActive
                      ? 'bg-white text-expo-dark font-bold shadow'
                      : 'text-white hover:bg-expo-primary font-medium',
                  ].join(' ')
                }
              >
                <Icon size={20} className="flex-none" />
                {!collapsed && <span className="whitespace-nowrap">{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* footer de marca + minimizar (minimizar solo en desktop) */}
      <div className="border-t border-white/15 p-4">
        {!collapsed && (
          <div className="mb-3 px-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/55">funciona con</span>
              <TomMark fontSize={18} dot="#FFC400" />
            </div>
            <div className="text-[10.5px] text-white/40 pt-1 tracking-wide">Tom Marketing &amp; Software</div>
          </div>
        )}
        {!mobile && (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expandir' : 'Minimizar'}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/85 text-sm py-2.5 transition-colors"
          >
            {collapsed ? (
              <ChevronRight size={16} />
            ) : (
              <>
                <ChevronLeft size={16} />
                <span>Minimizar</span>
              </>
            )}
          </button>
        )}
      </div>
    </aside>
  )
}
